import type { ReactNode } from "react";

import { Trans, useLingui } from "@lingui/react/macro";
import { observer } from "mobx-react-lite";
import { useCallback, useMemo } from "react";

import { useDialog } from "../../../../components/ui-kit/dialog-provider";
import { DataModel } from "../../../../models/data-model";
import { Tooltip } from "../../base/tooltip";
import { Button } from "../../button";

export const CellDeleteButton = observer(function CellDeleteButton({
  model,
  index,
  item,
  onConfirm,
  tooltip,
  disabled,
  children,
}: {
  model: DataModel;
  index: () => number;
  /**
   * The row being deleted. Prefer passing this over relying on the index-based
   * `model.paginatedData[index()]` lookup whenever the rendered rows aren't
   * driven by the model's own pagination (e.g. DataTable, where TanStack owns
   * sort/filter/pagination so `paginatedData` is stale and the index resolves
   * to the wrong row). When omitted, falls back to the index lookup.
   */
  item?: object;
  onConfirm: () => void;
  tooltip?: string;
  disabled?: boolean;
  children?: ReactNode;
}) {
  const { t } = useLingui();
  const validationMessage = useMemo(() => {
    const row = item ?? model.paginatedData[index()];
    const message = tooltip ?? (row ? model.validateItemDelete(row) : false);
    return message || undefined;
  }, [model, index, item, tooltip]);

  const { confirm } = useDialog();
  const handleClick = useCallback(async () => {
    if (await confirm(t`Are you sure you want to delete this row?`)) {
      onConfirm();
    }
  }, [confirm, onConfirm, t]);
  const isDeleteDisabled =
    disabled || !!validationMessage || model.editingIndex === -1;

  return (
    <Tooltip content={validationMessage}>
      <Button
        disabled={isDeleteDisabled}
        onClick={handleClick}
        variant="subtle"
        size="sm"
        aria-label={t`Delete`}
      >
        {children ?? <Trans>Delete</Trans>}
      </Button>
    </Tooltip>
  );
});
